import { useQuery } from '@tanstack/react-query';

import type { PaginatedResponse, TeamTableRow } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { teamEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Search team members parameters
 */
export interface SearchTeamMembersParams {
	limit?: number;
	page?: number;
	status?: string;
	sort?: string;
	timezone?: string;
}

/**
 * API function to search team members with optional filters and pagination
 * @param servicesProviderId - Required services provider ID
 * @param params - Optional search parameters
 */
export const searchTeamMembers = async (
	servicesProviderId: string,
	params: SearchTeamMembersParams = {},
): Promise<PaginatedResponse<TeamTableRow>> => {
	const response = await apiClient.post<PaginatedResponse<TeamTableRow>>(
		teamEndpoints.search(servicesProviderId),
		params,
	);
	return response.data;
};

/**
 * Hook for searching team members with optional filters and pagination
 */
export const useSearchTeamMembers = (
	params: SearchTeamMembersParams = {},
): ReturnType<typeof useQuery<PaginatedResponse<TeamTableRow>>> => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.team.list(servicesProviderId, params).queryKey,
		queryFn: () => searchTeamMembers(servicesProviderId, params),
		enabled: Boolean(servicesProviderId),
		staleTime: Infinity, // never stale unless the data is mutated
		...errorBoundaryOptions,
	});
};
